import * as Sentry from "@sentry/react";
import APIGenerated from "@/app/api/client";
import { AbstractClient, APIError } from "@/app/client";
import { UserCreate } from "@/app/types";
import { del } from "idb-keyval";
import { apiDelayMiddleware } from "@/lib/api-delay-middleware";

// Determine API URL based on environment and context
function getApiUrl(): string {
  // First check for VITE_API_URL (set at build time)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // For production domains
  if (
    typeof window !== "undefined" &&
    (window.location.hostname.endsWith("stockline.net") ||
      window.location.hostname.endsWith("stockline.ai"))
  ) {
    return `${window.location.protocol}//api.${window.location.hostname}`;
  }

  // In dev, proxy through Vite to avoid CORS (see vite.config.ts)
  if (import.meta.env.DEV) {
    return "/api";
  }

  // Default to same host with port 8000
  return `http://${window.location.hostname}:8000`;
}

const API_URL = getApiUrl();

let bearerToken: string | null = null;

// Legacy tokens embedded the merged feature-flag dict in the `ff` claim and
// could exceed 15 KB. Node's default max-http-header-size is 16 KB, so those
// tokens cause `431 Request Header Fields Too Large` through the Vite proxy
// before a request ever reaches the backend. New tokens are ~300 bytes —
// anything above this threshold is a stale legacy token, so we drop it and
// force a fresh login.
const MAX_TOKEN_BYTES = 4096;

function getToken(): string | null {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("bearer");
    if (stored && stored.length > MAX_TOKEN_BYTES) {
      localStorage.removeItem("bearer");
      bearerToken = null;
      return null;
    }
    bearerToken = stored;
  }
  return bearerToken;
}

function setToken(token: string) {
  bearerToken = token;
  if (typeof window !== "undefined") {
    localStorage.setItem("bearer", token);
  }
}

// End any super-admin impersonation markers, so logout / 401 / re-login can't
// leave the banner active for the next session. Keys + event name mirror
// lib/impersonation.ts (kept literal to avoid a circular import — that module
// depends on this one). `bearer_admin` is dropped to clean up the legacy stash
// from pre-hardening builds; the current flow never writes it. The dispatched
// event lets the banner react same-tab without a reload.
function clearImpersonationMarkers() {
  if (typeof window === "undefined") return;
  localStorage.removeItem("bearer_admin");
  localStorage.removeItem("impersonating_email");
  window.dispatchEvent(new Event("impersonation-change"));
}

function clearToken() {
  bearerToken = null;
  if (typeof window !== "undefined") {
    localStorage.removeItem("bearer");
  }
  clearImpersonationMarkers();
}

class ApiClientFetcher extends AbstractClient {
  constructor() {
    super();
  }

  async _fetch(params: {
    url: string;
    method: string;
    params?: Record<string, any>;
    headers?: Record<string, any>;
    bodyMime?:
      | "application/json"
      | "multipart/form-data"
      | "application/x-www-form-urlencoded";
    body?: Record<string, any> | FormData;
    auth?: string[];
  }): Promise<Response> {
    let query = "";
    if (params.params) {
      function flattenObject(obj: Record<string, any>): Record<string, string> {
        return Object.entries(obj).reduce(
          (acc, [key, value]) => {
            if (typeof value === "object" && value !== null) {
              Object.assign(acc, flattenObject(value));
            } else {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, string>,
        );
      }
      query =
        "?" +
        new URLSearchParams(
          Object.fromEntries(
            Object.entries(flattenObject(params.params)).filter(
              ([k, v]) => v !== undefined,
            ),
          ),
        ).toString();
    }
    let url = API_URL + params.url + query;
    let headers = params.headers || {};
    let init: RequestInit = {
      method: params.method,
    };
    if (params.method !== "GET") {
      if (params.bodyMime === "application/json") {
        headers["Content-Type"] = params.bodyMime;
        init.body = JSON.stringify(params.body);
      } else if (params.bodyMime === "application/x-www-form-urlencoded") {
        headers["Content-Type"] = params.bodyMime;
        init.body = new URLSearchParams(
          (params.body as Record<string, string>) || {},
        ).toString();
      } else if (params.bodyMime === "multipart/form-data") {
        // Check if body is already FormData
        if (params.body instanceof FormData) {
          // Use it directly - don't set Content-Type header, let browser set it with boundary
          init.body = params.body;
        } else {
          // Create new FormData from plain object
          let formData: FormData = new FormData();
          for (const key of Object.keys(params.body || {})) {
            const value = params.body![key];
            if (Array.isArray(value)) {
              // Handle arrays (like files)
              value.forEach((item) => {
                formData.append(key, item);
              });
            } else {
              formData.append(key, value);
            }
          }
          init.body = formData;
        }
      }
    }
    if (params.auth) {
      if (params.auth.includes("OAuth2PasswordBearer")) {
        if (!getToken()) {
          throw new APIError(401, "No token");
        }
        headers["Authorization"] = `Bearer ${getToken()}`;
      }
    }

    // Add Sentry trace headers for distributed tracing
    // This propagates the trace context from frontend to backend
    const activeSpan = Sentry.getActiveSpan();
    if (activeSpan) {
      const traceHeader = Sentry.spanToTraceHeader(activeSpan);
      if (traceHeader) {
        headers["sentry-trace"] = traceHeader;
      }
      const baggage = Sentry.spanToBaggageHeader(activeSpan);
      if (baggage) {
        headers["baggage"] = baggage;
      }
    }

    init.headers = headers;

    // Apply artificial delay middleware (for testing race conditions in dev/staging)
    let requestId = "";
    try {
      // Delay BEFORE sending request (simulates slow network initiation)
      requestId = await apiDelayMiddleware.delayBefore(url, init);
    } catch (error) {
      // Middleware error - continue without delay
      console.warn("[API] Middleware delayBefore failed, continuing:", error);
    }

    let res = await fetch(url, init);

    try {
      // Delay AFTER receiving response (simulates slow server response)
      res = await apiDelayMiddleware.delayAfter(requestId, res);
    } catch (error) {
      // Middleware error - continue with response
      console.warn("[API] Middleware delayAfter failed, continuing:", error);
    }

    if (!res.ok) {
      // Clear token on 401 Unauthorized to prevent redirect loops
      if (res.status === 401) {
        clearToken();
      }
      throw new APIError(res.status, await res.text());
    }
    return res;
  }
}

class ApiClient extends APIGenerated {
  constructor() {
    super(new ApiClientFetcher());
  }
  logOut() {
    clearToken();
  }

  async logIn(username: string, password: string) {
    let res = await this.postToken({
      username,
      password,
    });
    // Clear persisted query cache before setting token
    // This prevents stale auth error state from being restored on page reload
    try {
      await del("REACT_QUERY_OFFLINE_CACHE");
    } catch {
      // Ignore - IndexedDB might not be available
    }
    // A fresh login is a new identity — drop any lingering impersonation marker
    // so the banner doesn't claim the previous impersonated session.
    clearImpersonationMarkers();
    setToken(res.access_token);
  }

  async signUp(create: UserCreate) {
    let res = await this.postUsers(create);
    // Clear persisted query cache before setting token
    try {
      await del("REACT_QUERY_OFFLINE_CACHE");
    } catch {
      // Ignore - IndexedDB might not be available
    }
    clearImpersonationMarkers();
    setToken(res.access_token);
  }

  getToken = getToken;
  API_URL = API_URL;

  async getUsersBatch(userIds: string[]) {
    if (userIds.length === 0) return [];
    const ids = userIds.join(",");
    return await this.users.getBatch({ ids });
  }
}

const client = new ApiClient();
export default client;
export { setToken as setAuthorizationToken };
